/**
 * Selection-driven node-graph inspection, as a DI service.
 *
 * Depends on {@link IHierarchyService} and reacts to selection changes itself. On
 * every selection change it *cheaply* collects the NodeMaterials across the
 * selection (dedup, no node-tree walk) so the panel knows whether/what to show,
 * and only builds the heavy {@link NodeGraph}s **lazily** when a view reads
 * {@link graphs} — so nothing is parsed while the panel is minimized/hidden.
 * Hosts additionally report tab visibility via {@link setConsumerVisible}: while
 * hidden, even a `graphs` read won't build (the pending selection is only
 * remembered), and becoming visible re-signals so the view builds it then. The
 * follow/lock policy lives in the domain-agnostic {@link NodeGraphSelectionController};
 * this service wires it to the hierarchy and the material resolver, memoizes the
 * built graphs, and surfaces the safety-cap truncation via the log + a notification.
 */

import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { IGpuTimingService } from "../common/gpuTiming.js";
import { IPerformanceService } from "../../../editor/services/performance/common/performance.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { IInstantiationService } from "../../instantiation/common/instantiation.js";
import { IResourceService, type IResourceEntry } from "../../resources/common/resources.js";
import { IPreviewService } from "../../preview/common/preview.js";
import { buildResourceGraph } from "../../resources/common/nodegraph.resources.js";
import type { AnimationActionInfo, MixerAnimationInfo } from "../../resources/common/nodegraph.resources.js";
import { estimateTextureMemory, textureFormatLabel, textureMipLabel, textureSizeLabel } from "./helper.textures.js";
import { estimateGeometryMemory } from "./helper.geometries.js";
import { estimateResourcesMemory } from "./helper.memory.js";
import { isTexture, isGeometry, isAnimationMixer, isAnimationActionLike } from "./helper.js";
import type { CodeOption } from "../../code/common/codeView.js";
import { IHierarchyService } from "../../hierarchy/common/hierarchy.js";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { SelectNodeCommand } from "../../hierarchy/common/treeCommands.js";
import { ILogService } from "../../log/common/log.js";
import { INotificationService } from "../../notifications/common/notifications.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { IIdentitiyService } from "../../auth/common/auth.js";
import { ILicenseService } from "../../auth/common/licenses.js";
import { ICommandService } from "../../commands/common/commands.js";
import { IEditEntitlementService } from "../../upsell/common/editEntitlement.js";
import { Emitter, type Event } from "../../../base/common/events.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { IContextMenuService } from "../../contextview/common/context.js";
import { IInspectionService, type InspectionTargetRef } from "../../inspection/common/inspection.js";
import { Disposable, toDisposable, type IDisposable } from "../../../base/common/lifecycle.js";
import { NodeGraphSelectionController } from "../../nodegraph/selectionController.js";
import type { NodeGraph } from "../../nodegraph/model.js";
import { collectNodeMaterials, buildMaterialGraphs, builtMaterialToGraph, MAX_TOTAL_GRAPH_NODES, isFnCallNode, isLoopNode, fnCallToGraph, loopBodyToGraph, expandFnCall, uniqueMaterialLabels, type FnBuildContext } from "./nodegraph.tsl.js";
import { emitNodeTsl } from "./nodegraph.tsl.code.js";
import { collectRenderers, captureRenderGraph, frameResultToGraph } from "./nodegraph.rendergraph.js";
import { RenderCapture, type CapturedResource, type RenderCaptureResult, type RenderParticipant } from "./renderCapture.js";
import { isRenderer } from "./helper.js";
import { isUniformNode, isNodeMaterial, forceNodeMaterialRecompile, type NodeMaterialLike, type MaterialRecompileHandle } from "./helper.nodes.js";
import { IPropertyAccessorService } from "../../properties/common/accessor.js";
import { IPropertyService, Property, type PropertyChangedEvent } from "../../properties/common/properties.js";
import { getMetadata } from "./propertyProvider.metadata.js";
import { buildAddress } from "../../properties/common/propertyPath.js";
import { GraphObjectTargets } from "../../nodegraph/objectTargets.js";
import { IObjectTargetService } from "../../../editor/services/project/common/documentTarget.js";
import { propertyOpTargetVia } from "../../../editor/services/operations/common/propertyTarget.js";
import "./nodeGraphCommands.js"; // registers the node context-menu commands

export const INodeGraphInspectionService = createDecorator<INodeGraphInspectionService>("nodeGraphInspectionService");

/**
 * What the node-graph panel currently SHOWS, as reported by the UI (which owns
 * the drill-down navigation stack). Strings only — never live graph/material
 * references (the weak-registry rule) — so it's safe to hold and to hand to
 * consumers like the AI editor-state context.
 */
export interface NodeGraphNavigationState {
    /** Breadcrumb titles, root first (e.g. ["Render Graph", "Scene materials", "Fn Call"]). */
    readonly path: string[];
    /** The graphs of the CURRENT (deepest) level. */
    readonly graphs: { id: string; title: string }[];
}

export interface INodeGraphInspectionService {
    readonly _serviceBrand: undefined;
    /** Fires (as a signal) whenever the selection/lock changes; read the getters after. */
    readonly onDidChange: Event<void>;
    /** Whether the current selection exposes any node material (cheap — no build). */
    readonly hasContent: boolean;
    /** Number of node materials in the current selection (cheap — no build). */
    readonly materialCount: number;
    /** The graphs for the current selection — **built lazily & memoized** on first read. */
    readonly graphs: NodeGraph[] | null;
    /**
     * The single backing material to render a preview for, or null when there
     * isn't exactly one (multiple materials, or explicit/override graphs). Multi
     * material preview is deferred — see the plan.
     */
    readonly previewMaterial: NodeMaterialLike | null;
    readonly locked: boolean;
    setLocked(locked: boolean): void;
    toggleLock(): void;
    /**
     * Report whether a view is currently showing the graphs (e.g. the dock
     * panel's tab visibility). While hidden, {@link graphs} returns the
     * memoized result without building — so selection changes never trigger
     * TSL parsing while the panel is hidden; the pending selection is only
     * remembered (cheap). Turning visible fires {@link onDidChange} so views
     * re-read (and build) the current selection then. Defaults to visible,
     * which keeps always-visible hosts (classic/compact layout) working
     * without any wiring.
     */
    setConsumerVisible(visible: boolean): void;
    /**
     * Fires while an AnimationMixer graph is on screen, as its live values are
     * re-read (~{@link ANIMATION_TICK_MS}). Separate from {@link onDidChange} on
     * purpose: the graph TOPOLOGY is unchanged, so views must patch the affected
     * values instead of rebuilding (the level is otherwise a pure function of
     * the graphs, remounted per selection).
     */
    readonly onDidChangeAnimation: Event<void>;
    /**
     * Live animation values keyed `${graphIndex}:${nodeId}`, or null when nothing
     * animated is displayed. Read after {@link onDidChangeAnimation}.
     */
    getLiveAnimation(): ReadonlyMap<string, LiveNodeState> | null;
    /**
     * Show explicit graphs directly, ignoring the selection until it next
     * changes. Used by the "View Node Graph" material action and the dev sample.
     */
    showGraphs(graphs: NodeGraph[] | null): void;
    /**
     * Whether the current selection contains renderers, i.e. a frame capture
     * (rendergraph) is what's shown / can be refreshed.
     */
    readonly canCapture: boolean;
    /**
     * Re-captures the frame for the currently selected renderers (the panel's
     * update button). No-op when the selection has no renderers. Selecting a
     * renderer captures automatically once; this refreshes the snapshot.
     */
    recapture(): void;
    /**
     * The most recent rendered-frame graph for any of the given renderers —
     * the standing observer's last-frame snapshot (or an earlier demanded
     * capture). Synchronous and read-only: never arms a capture, never waits.
     * Null when none of the renderers has rendered since the editor attached.
     * Used by consumers that can't wait for a frame (e.g. the AI graph tool).
     */
    getRenderGraph(renderers: readonly object[]): NodeGraph | null;
    /** The exact last observed render calls, for consumers that need live pass objects. */
    getLastRenderFrame(renderer: object): RenderCaptureResult | null;
    /** Joins the standing renderer wrapper without installing another host hook. */
    registerRenderParticipant(participant: RenderParticipant): IDisposable;
    /**
     * Resolves the child graphs to navigate INTO for a double-clicked node, or
     * null when the node isn't steppable. Currently: any node that carries a
     * scene — a **scene node** (`meta.resource`) or a **pass node** (`meta.
     * stepResource` = the scene that render call drew) — → the TSL graphs of the
     * node materials used anywhere in that scene's subtree. Read-only — builds
     * graphs but retains nothing. The returned graphs already have inline editors
     * attached (same as a directly-selected material).
     *
     * `codeTargets` are what the code panel should show WHILE this level is
     * visible (the stepped-into materials / the Fn body). The UI owns the
     * navigation stack, so it sets them as an inspection-service override on
     * every level change — stepping back OUT re-applies the parent level's.
     */
    stepInto(meta: Record<string, unknown> | null | undefined): { graphs: NodeGraph[]; title: string; codeTargets?: InspectionTargetRef[] } | null;
    /**
     * A context-menu command asks the panel to step into a node (e.g. "Open
     * shader graph" on a material node) — the command layer can't push a level
     * onto the panel's drill-down stack, so it fires this and the panel (which
     * owns the stack + the Pro intent-gate) drills. Carries the node's `meta`.
     */
    readonly onDidRequestStepInto: Event<Record<string, unknown>>;
    requestStepInto(meta: Record<string, unknown> | null | undefined): void;
    /**
     * The panel reports what it currently shows (it owns the drill-down stack);
     * null when no node-graph view is mounted. Read by consumers that describe
     * the editor state (e.g. the AI context) — strings only, never live objects.
     */
    reportNavigation(state: NodeGraphNavigationState | null): void;
    readonly navigation: NodeGraphNavigationState | null;
    /**
     * Selects the live object behind a graph node (its `meta.resource`, a
     * {@link CapturedResource}) in the hierarchy — e.g. clicking a scene/camera/
     * renderer node in a captured frame graph. Routed through the hierarchy's
     * `SelectNodeCommand`, so it participates in **undo/redo** like a click in
     * the hierarchy view. **Locks the panel first** (when not already locked):
     * the selection change would otherwise re-target the controller and replace
     * the very graph being clicked. Returns false when the node carries no
     * live resource or the resource isn't in the hierarchy (e.g. render targets).
     *
     * `domain`/`category`/`steppable` describe the clicked NODE and are used only
     * for telemetry — every call is recorded, including the ones that select
     * nothing, so we learn what people reach for inside a graph.
     */
    selectGraphNode(
        meta: Record<string, unknown> | null | undefined,
        options?: { multiSelect?: boolean; domain?: string; category?: string; steppable?: boolean },
    ): boolean;
}

/** Free-tier node budget for the resource graph — root + a couple of neighbours
 *  are detailed, the rest render redacted (the Pro tease). */
const RESOURCE_GRAPH_FREE_NODES = 3;

/** GPU + CPU bytes for a live texture/geometry ({0,0} for anything else) —
 *  injected into the pure resource-graph adapter so it stays three-free.
 *  Exported so the AI `nodegraph_read` tool reuses the same estimate. */
export function resourceMemory(resource: object): { cpu: number; vram: number } {
    if (isTexture(resource)) { const m = estimateTextureMemory(resource); return { cpu: m.cpu, vram: m.vram }; }
    if (isGeometry(resource)) { const m = estimateGeometryMemory(resource as never); return { cpu: m.cpu, vram: m.vram }; }
    return { cpu: 0, vram: 0 };
}

/** three's AnimationActionLoopStyles — kept local; the host's three is minified
 *  and we must not import constants we only need for a label. */
const LOOP_NAMES: Record<number, string> = { 2200: "Once", 2201: "Repeat", 2202: "PingPong" };

/** ~10Hz: fast enough to read a crossfade, slow enough to be invisible on the
 *  host page's frame budget (we are a guest in someone else's render loop). */
export const ANIMATION_TICK_MS = 100;

/** Live per-node animation values pushed between graph rebuilds. Action nodes
 *  carry weight/progress/running; the mixer root carries the blend summary. */
export interface LiveNodeState {
    running?: boolean;
    paused?: boolean;
    /** 0..1 effective blend weight (action nodes) */
    weight?: number;
    weightLabel?: string;
    /** 0..1 playhead through the clip (action nodes) */
    progress?: number;
    timeLabel?: string;
    /** mixer root: how many actions are actually blending */
    activeCount?: number;
    actionCount?: number;
}

/** Live animation state of an AnimationMixer, or undefined for anything else —
 *  injected into the pure resource-graph adapter so it stays three-free.
 *  Re-read on a timer while a mixer graph is visible, so weights animate.
 *  Every access is defensive: these are HOST objects and `_actions` is private
 *  three API (already duck-typed by `isAnimationMixer`).
 *  Exported so the AI `nodegraph_read` tool reports the same state. */
export function mixerAnimation(resource: object): MixerAnimationInfo | undefined {
    if (!isAnimationMixer(resource)) return undefined;
    const actions: AnimationActionInfo[] = [];
    try {
        for (const raw of resource._actions ?? []) {
            if (!isAnimationActionLike(raw)) continue;
            const a = raw as typeof raw & {
                paused?: boolean; weight?: number; timeScale?: number;
                getEffectiveWeight?(): number; getEffectiveTimeScale?(): number;
            };
            let clipName = ""; let duration = 0;
            try {
                const clip = a.getClip();
                clipName = clip?.name ?? "";
                duration = typeof clip?.duration === "number" ? clip.duration : 0;
            } catch { /* host object */ }
            // EFFECTIVE weight is what actually reaches the blend (it folds in
            // enabled/fade state); plain `.weight` ignores a fade in progress.
            let weight = 0;
            try { weight = a.getEffectiveWeight?.() ?? a.weight ?? 0; } catch { weight = a.weight ?? 0; }
            let timeScale = 1;
            try { timeScale = a.getEffectiveTimeScale?.() ?? a.timeScale ?? 1; } catch { timeScale = a.timeScale ?? 1; }
            let running = false;
            try { running = a.isRunning() === true; } catch { /* host object */ }
            actions.push({
                clipName,
                duration,
                time: typeof a.time === "number" ? a.time : 0,
                weight: typeof weight === "number" ? weight : 0,
                timeScale: typeof timeScale === "number" ? timeScale : 1,
                running,
                paused: a.paused === true,
                ...(LOOP_NAMES[a.loop] ? { loop: LOOP_NAMES[a.loop] } : {}),
            });
        }
    } catch { /* host object — show whatever we collected */ }
    return {
        time: typeof resource.time === "number" ? resource.time : 0,
        timeScale: typeof resource.timeScale === "number" ? resource.timeScale : 1,
        actions,
    };
}

/** What the selection resolves to: node materials (TSL graphs), renderers
 *  (frame capture), or a bare resource — a texture/geometry with no shader
 *  graph shows its resource+memory DEPENDENCY graph instead. */
type GraphSource =
    | { readonly kind: "material"; readonly material: NodeMaterialLike }
    | { readonly kind: "renderer"; readonly renderer: object }
    | { readonly kind: "resource"; readonly entry: IResourceEntry };

export class NodeGraphInspectionService extends Disposable implements INodeGraphInspectionService {
    declare readonly _serviceBrand: undefined;

    private readonly _onDidChange = this._register(new Emitter<void>());
    readonly onDidChange = this._onDidChange.event;
    private readonly _onDidRequestStepInto = this._register(new Emitter<Record<string, unknown>>());
    readonly onDidRequestStepInto = this._onDidRequestStepInto.event;

    requestStepInto(meta: Record<string, unknown> | null | undefined): void {
        if (meta && typeof meta === "object") this._onDidRequestStepInto.fire(meta);
    }

    private readonly _controller: NodeGraphSelectionController<GraphSource>;
    private _graphsCache: NodeGraph[] | null = null;
    private _built = false;
    /** Captured frame graph for the selected renderers; null until a capture lands. */
    private _capturedGraph: NodeGraph | null = null;
    /**
     * Last successful capture per participating renderer — so on-demand pages
     * that pause rendering still show something: selecting a renderer that
     * isn't currently producing frames shows its most recent snapshot
     * immediately (and re-selecting shows the graph seen moments ago). A fresh
     * capture still runs and replaces the snapshot when the page renders again.
     * WeakMap keys so the cache never keeps a page renderer alive; the graph
     * itself holds only primitive snapshots + weak `deref()`s.
     */
    private readonly _lastCaptureByRenderer = new WeakMap<object, NodeGraph>();
    /** The cached snapshot currently displayed, replaced when a capture lands. */
    private _displayedCachedCapture: NodeGraph | null = null;
    /**
     * Renderers with a capture currently armed. Captures wrap renderer methods,
     * and two concurrent captures on the same renderer would nest/clobber each
     * other's wraps — so any capture (demanded or prime) skips renderers that
     * are already armed; the armed capture's resolution serves both. Strong
     * refs, but strictly transient (cleared when the capture settles).
     */
    private readonly _renderersInFlight = new Set<object>();
    /** Renderers already handed to the standing observer (observe once each). */
    private readonly _observedRenderers = new WeakSet<object>();
    /**
     * Iterable weak handles to every observed renderer — the WeakSet/WeakMap above
     * can't be enumerated, and resolving a material's renderer (see
     * {@link _resolveFnBuildContext}) needs to scan them. WeakRefs, so this never
     * extends a renderer's lifetime; dead entries are skipped on scan.
     */
    private readonly _observedRendererRefs: WeakRef<object>[] = [];
    /**
     * The standing last-frame observer: permanently wraps every discovered
     * renderer and keeps each one's most recent rendered frame. A capture-scoped
     * recorder cannot see frames that happened before it armed — pages that
     * render at load and then pause would be undebuggable without this.
     */
    private _observer: RenderCapture | null = null;
    private readonly _renderParticipants = new Set<RenderParticipant>();
    /** Most recent rendered frame per renderer, from the standing observer. */
    private readonly _lastFrameByRenderer = new WeakMap<object, RenderCaptureResult>();
    /** Bumped on every invalidation so a late capture result can detect staleness. */
    private _generation = 0;
    /** Explicit graphs to show, bypassing selection (sample / action). Cleared on selection change. */
    private _override: NodeGraph[] | null = null;
    /** Last skip count, so we only notify on the rising edge (avoid spam). */
    private _lastSkipped = 0;
    /** Whether a view is showing the graphs — hidden consumers must not build. */
    private _consumerVisible = true;

    get locked(): boolean { return this._controller.locked; }
    get hasContent(): boolean {
        return this._override ? true : !!(this._sourceMaterials?.length || this._sourceRenderers?.length || this._sourceResources?.length);
    }
    get materialCount(): number {
        if (this._override) return this._override.length;
        return (this._sourceMaterials?.length ?? 0) + (this._sourceRenderers?.length ?? 0) + (this._sourceResources?.length ?? 0);
    }
    get canCapture(): boolean { return !!this._sourceRenderers?.length; }

    /** The materials the graphs are built from (an inspected material arrives
     *  through the same base targets as a selection — see the constructor). */
    private get _sourceMaterials(): readonly NodeMaterialLike[] | null {
        const materials = this._controller.items
            ?.filter((s): s is Extract<GraphSource, { kind: "material" }> => s.kind === "material")
            .map((s) => s.material);
        return materials?.length ? materials : null;
    }

    /** The renderers whose frame the graphs include. */
    private get _sourceRenderers(): readonly object[] | null {
        const renderers = this._controller.items
            ?.filter((s): s is Extract<GraphSource, { kind: "renderer" }> => s.kind === "renderer")
            .map((s) => s.renderer);
        return renderers?.length ? renderers : null;
    }

    /** Bare resources (texture/geometry) → their dependency+memory graph. */
    private get _sourceResources(): readonly IResourceEntry[] | null {
        const entries = this._controller.items
            ?.filter((s): s is Extract<GraphSource, { kind: "resource" }> => s.kind === "resource")
            .map((s) => s.entry);
        return entries?.length ? entries : null;
    }

    /** Optional (not every product registers it) — resolved lazily to avoid the
     *  DI construction-order trap (see plans/di-unknown-service-guard.md). */
    private get _resources(): IResourceService | null {
        try { return this.instantiation.invokeFunction((a) => a.get(IResourceService)); } catch { return null; }
    }
    private get _preview(): IPreviewService | null {
        try { return this.instantiation.invokeFunction((a) => a.get(IPreviewService)); } catch { return null; }
    }

    /** A lazy texture thumbnail via the preview service — the resource graph's
     *  injected `previewFor` (adapter stays preview-free). Cached per texture by
     *  the preview service; the graph node holds only the produce closure. */
    private _resourcePreview(resource: object): { produce: () => Promise<string | null>; aspect?: number } | undefined {
        const preview = this._preview;
        if (!preview || !isTexture(resource) || !preview.canPreview(resource)) return undefined;
        const image = (resource as { image?: { width?: number; height?: number } }).image;
        const aspect = image?.width && image?.height ? image.width / image.height : undefined;
        return {
            aspect,
            produce: () => preview.getPreview(resource).then((r) => r?.url ?? null).catch(() => null),
        };
    }

    get previewMaterial(): NodeMaterialLike | null {
        if (this._override) return null;
        if (this._sourceRenderers?.length) return null;
        const items = this._sourceMaterials;
        return items && items.length === 1 ? items[0] : null;
    }

    get graphs(): NodeGraph[] | null {
        if (this._override) return this._override;
        // Hidden consumer (e.g. a background dock tab): never build here — just
        // return whatever is memoized (null after a selection change, since
        // _invalidate drops the cache). setConsumerVisible(true) fires a change
        // so the view re-reads and builds the pending selection at that point.
        if (!this._consumerVisible) return this._graphsCache;
        if (!this._built) {
            const built: NodeGraph[] = [];
            const animationSources: typeof this._animationSources = [];
            const materials = this._sourceMaterials;
            if (materials && materials.length) {
                this.logger.info(`[nodegraph] Parsing ${materials.length} material graph${materials.length === 1 ? "" : "s"}`);
                const { graphs, skipped } = buildMaterialGraphs(materials);
                this.analytics.logEvent("nodegraph.parse", {
                    materials: materials.length,
                    nodes: graphs.reduce((sum, g) => sum + g.nodes.length, 0),
                    materialType: (materials[0] as { type?: string })?.type ?? "unknown",
                    loggedIn: this.identity.isLoggedIn,
                });
                // Attach editable IProperty instances to leaf value nodes so the
                // renderer can reuse the Inspector's editors (with undo/redo), and
                // mark Fn nodes steppable (their renderer is resolved lazily).
                graphs.forEach((graph, i) => {
                    this._attachEditors(graph, materials[i]);
                    this._markFnNodes(graph, materials[i]);
                });
                built.push(...graphs);
                this._reportSkipped(skipped);
            }
            // Selected renderers → the frame's rendergraph. The capture is async
            // (it records the next animation frame), so the first read kicks it
            // off and the result is appended + signaled when it lands.
            const renderers = this._sourceRenderers;
            if (renderers?.length) {
                if (this._capturedGraph) built.push(this._capturedGraph);
                else {
                    // On-demand pages may not produce another frame for a long
                    // time (or ever, while paused) — show the last snapshot for
                    // this renderer immediately; the capture below replaces it
                    // whenever the page actually renders again.
                    const cached = this._lastCaptureFor(renderers);
                    if (cached) {
                        this._displayedCachedCapture = cached;
                        built.push(cached);
                    }
                    this._startCapture(renderers);
                }
            }
            // Bare resources (texture/geometry) → their dependency+memory graph.
            const resourceEntries = this._sourceResources;
            const resources = this._resources;
            if (resourceEntries?.length && resources) {
                // Pro gate: free users see the first few nodes, the rest render
                // redacted (locked) — same nodes/edges/totals, identity stripped.
                const licensed = this.license.isInspectorAccessAllowed;
                for (const entry of resourceEntries) {
                    const graph = buildResourceGraph(entry, resources, {
                        memoryFor: resourceMemory,
                        dedupedMemoryFor: estimateResourcesMemory,
                        previewFor: (r) => this._resourcePreview(r),
                        formatFor: (r) => (isTexture(r) ? textureFormatLabel(r) : undefined),
                        sizeFor: (r) => (isTexture(r) ? textureSizeLabel(r) : undefined),
                        mipsFor: (r) => (isTexture(r) ? textureMipLabel(r) : undefined),
                        animationFor: mixerAnimation,
                        redactAfter: licensed ? null : RESOURCE_GRAPH_FREE_NODES,
                    });
                    // A material node whose live material is a node material can
                    // drill into its TSL shader graph (double-click / context menu,
                    // Pro-gated). Stamp it here — the adapter is three-free and
                    // can't tell a node material from a plain one. Redacted nodes
                    // lose meta.resource, so they're skipped (not steppable).
                    for (const node of graph.nodes) {
                        const res = node.meta?.resource as { kind?: string; deref?: () => object | undefined } | undefined;
                        const live = res?.kind === "material" ? res.deref?.() : undefined;
                        if (live && isNodeMaterial(live)) node.meta!.canStepToShader = true;
                    }
                    // A mixer graph shows LIVE state (weights, playheads) — note it
                    // so the ticker re-reads just this entry. Keyed by graph INDEX,
                    // the same index toFlow stamps on every node, so views look
                    // values up without extra plumbing. Weak: only the entry is held.
                    if (graph.nodes[0]?.meta?.animation === true) {
                        animationSources.push({ graphIndex: built.length, rootNodeId: graph.nodes[0].id, entry });
                    }
                    built.push(graph);
                }
                this.analytics.logEvent("nodegraph.parse", {
                    domain: "resources",
                    resources: resourceEntries.length,
                    // WHAT was inspected. Kinds ("texture") and three types
                    // ("CompressedTexture") only — never resource NAMES, which on a
                    // third-party page are someone else's content, not our telemetry.
                    kinds: [...new Set(resourceEntries.map((e) => e.kind))].sort().join(","),
                    types: [...new Set(resourceEntries.map((e) => e.type))].sort().join(","),
                    // mixer graphs (live animation) vs plain dependency graphs
                    animated: animationSources.length,
                    licensed,
                    loggedIn: this.identity.isLoggedIn,
                });
            }
            this._graphsCache = built.length ? built : null;
            this._built = true;
            this._animationSources = animationSources;
            this._syncAnimationTicker();
        }
        return this._graphsCache;
    }

    private _startCapture(renderers: readonly object[]): void {
        // A capture is already armed for (some of) these renderers — its
        // resolution populates the snapshot cache and notifies; arming a second
        // one would nest the method wraps.
        if (renderers.some(r => this._renderersInFlight.has(r))) return;
        for (const renderer of renderers) this._renderersInFlight.add(renderer);
        const generation = this._generation;
        // "Waiting", not "capturing": on-demand pages may not render until the
        // user interacts — the capture stays armed until a frame actually runs.
        this.logger.info(`[nodegraph] Waiting for the next rendered frame of ${renderers.length} renderer${renderers.length === 1 ? "" : "s"}`);
        captureRenderGraph(renderers, undefined, () => this.gpuTiming?.last ?? null, this.performance?.targetFps).then((graph) => {
            for (const renderer of renderers) {
                this._renderersInFlight.delete(renderer);
                this._lastCaptureByRenderer.set(renderer, graph);
            }
            // Selection moved on while we waited for the frame — drop the result
            // from display (it's cached above); if the new selection also wants a
            // capture (its graphs read was blocked by the in-flight guard), kick
            // it off now.
            if (generation !== this._generation) {
                const current = this._sourceRenderers;
                if (this._built && !this._capturedGraph && current?.length) this._startCapture(current);
                return;
            }
            this._capturedGraph = graph;
            // The fresh frame supersedes a cached snapshot that may be on display.
            const rest = (this._graphsCache ?? []).filter(g => g !== this._displayedCachedCapture);
            this._displayedCachedCapture = null;
            this._graphsCache = [...rest, graph];
            this.analytics.logEvent("rendergraph.capture", {
                renderers: renderers.length,
                passes: graph.nodes.filter(n => n.category === "pass").length,
                nodes: graph.nodes.length,
            });
            this._onDidChange.fire();
        }).catch((error) => {
            for (const renderer of renderers) this._renderersInFlight.delete(renderer);
            this.logger.warn(`[nodegraph] Frame capture failed: ${error instanceof Error ? error.message : error}`);
        });
    }

    /**
     * Hands a renderer to the standing observer the moment it appears in the
     * hierarchy — no UI has to demand anything. From then on its most recent
     * rendered frame is always available (see {@link _lastFrameByRenderer}), so
     * on-demand pages that pause stay debuggable. The observer patches FIRST
     * (discovery precedes any selection), which keeps its wraps safely below
     * any demanded capture's.
     */
    private _observeRenderer(owner: unknown): void {
        if (!isRenderer(owner) || this._observedRenderers.has(owner)) return;
        this._observedRenderers.add(owner);
        this._observedRendererRefs.push(new WeakRef(owner));
        if (!this._observer) {
            this._observer = new RenderCapture({
                frames: "continuous",
                onFrame: (frame) => this._onObservedFrame(frame),
                renderParticipants: () => [...this._renderParticipants],
            });
            this._register({ dispose: () => this._observer?.cancel() });
            // Continuous observation: the promise intentionally never resolves.
            void this._observer.capture([owner]);
        } else {
            this._observer.addRenderers([owner]);
        }
    }

    /** Stores the observed frame as the last-known snapshot of each renderer it used. */
    private _onObservedFrame(frame: RenderCaptureResult): void {
        const seen = new Set<string>();
        for (const call of frame.frames[0]?.calls ?? []) {
            if (seen.has(call.renderer)) continue;
            seen.add(call.renderer);
            const live = (frame.resources.get(call.renderer) as CapturedResource | undefined)?.deref();
            if (live) this._lastFrameByRenderer.set(live, frame);
        }
    }

    getRenderGraph(renderers: readonly object[]): NodeGraph | null {
        return this._lastCaptureFor(renderers);
    }

    getLastRenderFrame(renderer: object): RenderCaptureResult | null {
        return this._lastFrameByRenderer.get(renderer) ?? null;
    }

    registerRenderParticipant(participant: RenderParticipant): IDisposable {
        this._renderParticipants.add(participant);
        return toDisposable(() => this._renderParticipants.delete(participant));
    }

    stepInto(meta: Record<string, unknown> | null | undefined): { graphs: NodeGraph[]; title: string; codeTargets?: InspectionTargetRef[] } | null {
        // TSL Fn call node → expand its body into its own graph. The material was
        // stamped on the node at build time; the renderer that can run the Fn body
        // is resolved lazily NOW, from the standing observer's frames (no coupling
        // to the three engine). Fails cleanly (→ toast) when no observed frame drew
        // this material — e.g. a paused page we joined after it last rendered.
        // A LOOP body is the same drill-down: three builds it on demand from a
        // throwaway builder (LoopNode.getProperties), so the level is produced by
        // the twin of fnCallToGraph and gets the same editor/Fn marking.
        if (meta?.node && isLoopNode(meta.node) && meta.fnMaterial) {
            const title = (typeof meta.fnTitle === "string" && meta.fnTitle) || "Loop";
            const ctx = this._resolveFnBuildContext(meta.fnMaterial);
            if (!ctx) return null;
            const graph = loopBodyToGraph(meta.node, ctx, title);
            if (!graph) return null;
            const loopMaterial = meta.fnMaterial as NodeMaterialLike;
            this._attachEditors(graph, loopMaterial);
            this._markFnNodes(graph, loopMaterial);
            const material = loopMaterial as { name?: string; type?: string };
            this.analytics.logEvent("nodegraph.step-into", { kind: "loop", ownerType: material.type ?? "unknown", loggedIn: this.identity.isLoggedIn });
            return {
                graphs: [graph],
                title,
                codeTargets: [{ target: meta.node as object, label: title }],
            };
        }
        if (meta?.node && isFnCallNode(meta.node) && meta.fnMaterial) {
            const title = (typeof meta.fnTitle === "string" && meta.fnTitle) || "Function";
            const ctx = this._resolveFnBuildContext(meta.fnMaterial);
            if (!ctx) return null;
            const graph = fnCallToGraph(meta.node, ctx, title);
            if (!graph) return null;
            // Same treatment every other graph level gets. Without the editor
            // pass, leaf uniforms inside an Fn body carry no property — and a
            // TEXTURE card draws its thumbnail through that property widget, so
            // textures rendered blank in here while previewing fine everywhere
            // else. Marking Fn nodes keeps nested Fn calls steppable too.
            const fnMaterial = meta.fnMaterial as NodeMaterialLike;
            this._attachEditors(graph, fnMaterial);
            this._markFnNodes(graph, fnMaterial);
            // Code panel targets for this level: the property system can't
            // describe a TSL node, so the Fn body's "TSL Code" option is handed
            // over explicitly; the owning material stays as a second section.
            const fnNode = meta.node as object;
            const material = meta.fnMaterial as { name?: string; type?: string };
            const fnCode: CodeOption[] = [{
                id: "fn:tsl-code",
                kind: "tsl-code",
                label: "TSL Code",
                group: title,
                category: "function",
                ownerType: material.type,
                // Lazy: re-expands (= runs) the Fn body only when shown. Nested
                // anonymous Fns inside the body expand too (same render ctx).
                produce: () => {
                    const body = expandFnCall(fnNode, ctx);
                    return body ? emitNodeTsl(body, title, (n) => expandFnCall(n, ctx)) : null;
                },
            }];
            const codeTargets: InspectionTargetRef[] = [
                { target: fnNode, label: title, payloads: { code: fnCode } },
                { target: meta.fnMaterial as object, label: material.name || material.type || "Material" },
            ];
            this.analytics.logEvent("nodegraph.step-into", { kind: "fn", ownerType: material.type ?? "unknown", loggedIn: this.identity.isLoggedIn });
            return { graphs: [graph], title, codeTargets };
        }

        // The material sink of a TSL graph → drill into the COMPILED node tree.
        // Same Pro gate as the shader step below: this is deep-inspect territory.
        if (meta?.canStepToBuilt === true) {
            if (!this.license.isInspectorAccessAllowed) {
                this.analytics.logEvent("nodegraph.step-into-locked", { kind: "material-built", loggedIn: this.identity.isLoggedIn });
                return null;
            }
            const ref = meta.builtMaterial as { deref?: () => object | undefined } | undefined;
            const material = typeof ref?.deref === "function" ? ref.deref() : undefined;
            if (!material || !isNodeMaterial(material)) return null;
            const graph = builtMaterialToGraph(material);
            if (!graph) return null;
            this._attachEditors(graph, material);
            this._markFnNodes(graph, material);
            const mat = material as { name?: string; type?: string };
            const label = mat.name || mat.type || "Material";
            this.analytics.logEvent("nodegraph.step-into", {
                kind: "material-built",
                ownerType: mat.type ?? "unknown",
                nodes: graph.nodes.length,
                loggedIn: this.identity.isLoggedIn,
            });
            return { graphs: [graph], title: `${label} — compiled`, codeTargets: [{ target: material as object, label }] };
        }

        // A material node in a dependency graph → drill into its TSL SHADER graph.
        // Pro-gated: the dependency graph is free, the shader internals are the
        // deep inspect (matches the compiled-shader / code-view gate). Returns
        // null when unlicensed — the caller shows the upsell.
        if (meta?.canStepToShader === true) {
            if (!this.license.isInspectorAccessAllowed) {
                this.analytics.logEvent("nodegraph.step-into-locked", { kind: "material-tsl", loggedIn: this.identity.isLoggedIn });
                return null;
            }
            const asMaterial = meta.resource as { deref?: () => object | undefined } | undefined;
            const material = typeof asMaterial?.deref === "function" ? asMaterial.deref() : undefined;
            if (!material || !isNodeMaterial(material)) return null;
            const { graphs } = buildMaterialGraphs([material]);
            if (!graphs.length) return null;
            this._attachEditors(graphs[0], material);
            this._markFnNodes(graphs[0], material);
            const mat = material as { name?: string; type?: string };
            const label = mat.name || mat.type || "Material";
            this.analytics.logEvent("nodegraph.step-into", { kind: "material-tsl", loggedIn: this.identity.isLoggedIn });
            return { graphs, title: `${label} — shader`, codeTargets: [{ target: material as object, label }] };
        }

        // A scene node holds the scene in `resource`; a pass node holds the scene
        // it drew in `stepResource`. Either way we drill into that scene.
        const resource = (meta?.stepResource ?? meta?.resource) as
            | { kind?: string; deref?: () => object | undefined }
            | undefined;
        const live = typeof resource?.deref === "function" ? resource.deref() : undefined;
        if (!live) return null;

        // Only scenes carry materials (cameras/renderers/targets/canvas don't).
        const isScene = resource?.kind === "scene" || (live as { isScene?: boolean }).isScene === true;
        if (!isScene) return null;

        // Walk the scene subtree; collectNodeMaterials dedups by reference.
        const objects: unknown[] = [];
        const traverse = (live as { traverse?: (cb: (o: unknown) => void) => void }).traverse;
        if (typeof traverse === "function") traverse.call(live, (o: unknown) => objects.push(o));
        else objects.push(live);
        const materials = collectNodeMaterials(objects);
        if (!materials.length) return null;

        const { graphs } = buildMaterialGraphs(materials);
        if (!graphs.length) return null;
        // Same inline editors + Fn-node marking a directly-selected material gets
        // (built graphs are a prefix of `materials`, so indices align even when
        // the cap truncates).
        graphs.forEach((graph, i) => {
            this._attachEditors(graph, materials[i]);
            this._markFnNodes(graph, materials[i]);
        });

        // Code panel targets for this level — every loaded graph becomes one
        // picker section (built graphs are a prefix of `materials`, so indices
        // align). Label by distinct material name/type (id-disambiguated when
        // several share a type) so same-typed materials don't merge into one
        // section in the picker.
        const labels = uniqueMaterialLabels(materials.slice(0, graphs.length));
        const codeTargets: InspectionTargetRef[] = graphs
            .map((graph, i) => ({ target: materials[i] as object, label: labels[i] }))
            .filter(r => !!r.target && typeof r.target === "object");

        // "pass" when a pass card drilled into the scene it drew (stepResource);
        // "scene" when a scene card was opened directly.
        const kind = meta?.stepResource ? "pass" : "scene";
        this.analytics.logEvent("nodegraph.step-into", { kind, materials: materials.length, graphs: graphs.length, loggedIn: this.identity.isLoggedIn });

        const name = (live as { name?: string }).name;
        return { graphs, title: name ? `${name} — materials` : "Scene materials", codeTargets };
    }

    /**
     * Tags each TSL `Fn` call node in a material graph as steppable and records
     * its material, so stepping in can (lazily) resolve the renderer to expand
     * the body. Marking is unconditional — whether a renderer can actually be
     * found is answered honestly at step time ({@link _resolveFnBuildContext}).
     */
    private _markFnNodes(graph: NodeGraph, material: NodeMaterialLike): void {
        for (const node of graph.nodes) {
            // A LOOP is expandable for the same reason and by the same means: its
            // body is built on demand from a throwaway builder. It carries the
            // material the same way, and differs only in which expander runs.
            if (isLoopNode(node.meta?.node)) {
                node.meta!.fnMaterial = material;
                node.meta!.fnTitle = node.title;
                node.meta!.canExpandLoop = true;
                continue;
            }
            if (!isFnCallNode(node.meta?.node)) continue;
            node.meta!.fnMaterial = material;
            node.meta!.fnTitle = node.title;
            node.meta!.canExpandFn = true;
        }
    }

    /**
     * Resolves the {@link FnBuildContext} for a material by scanning the standing
     * observer's most recent frame of each observed renderer for a scene that
     * contains a mesh using the material — the renderer that drew it (and its
     * camera). Deterministic and coupling-free (reads only rendergraph capture
     * data). Returns null when no observed frame drew the material — the honest
     * "we haven't seen this render yet" case, surfaced to the user as a toast.
     */
    private _resolveFnBuildContext(material: unknown): FnBuildContext | null {
        for (const ref of this._observedRendererRefs) {
            const renderer = ref.deref();
            if (!renderer) continue;
            const frame = this._lastFrameByRenderer.get(renderer);
            for (const call of frame?.frames[0]?.calls ?? []) {
                const scene = (frame!.resources.get(call.scene) as CapturedResource | undefined)?.deref();
                if (!scene || !sceneContainsMaterial(scene, material)) continue;
                const camera = (frame!.resources.get(call.camera) as CapturedResource | undefined)?.deref();
                return { renderer, scene, camera, material };
            }
        }
        return null;
    }

    recapture(): void {
        if (!this._sourceRenderers?.length) return;
        // Explicit user refresh — the resulting capture also logs rendergraph.capture.
        this.analytics.logEvent("rendergraph.recapture", { renderers: this._sourceRenderers.length });
        this._capturedGraph = null;
        this._built = false;
        this._graphsCache = null;
        this._onDidChange.fire();
    }

    showGraphs(graphs: NodeGraph[] | null): void {
        this._override = graphs && graphs.length ? graphs : null;
        this._onDidChange.fire();
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @ILogService private readonly logger: ILogService,
        @INotificationService private readonly notifications: INotificationService,
        @IPropertyAccessorService private readonly accessor: IPropertyAccessorService,
        @IAnalyticsService private readonly analytics: IAnalyticsService,
        @IIdentitiyService private readonly identity: IIdentitiyService,
        @ILicenseService private readonly license: ILicenseService,
        @ICommandService private readonly commands: ICommandService,
        @IEditEntitlementService private readonly entitlement: IEditEntitlementService,
        @IInspectionService inspection: IInspectionService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
        @IContextKeyService contextKeyService: IContextKeyService,
        @IContextMenuService contextMenuService: IContextMenuService,
        // the LAST GPU measurement, when one was taken — puts each pass's cost on
        // its card. Never measures by itself: profiling is the user's call.
        @IGpuTimingService private readonly gpuTiming: IGpuTimingService,
        // only for the display's frame budget: what makes a pass's time good or bad
        @IPerformanceService private readonly performance: IPerformanceService,
        @IObjectTargetService private readonly objectTargets: IObjectTargetService,
    ) {
        super();
        this._graphTargets = new GraphObjectTargets(owner => {
            const resolved = propertyOpTargetVia({ targets: this.objectTargets, hierarchy: this.hierarchy,
                logger: undefined, projects: undefined }, { owner, key: "" });
            if (!resolved) return null;
            return { ...resolved.target, pathPrefix: (resolved.target.pathPrefix ?? "") + resolved.path };
        });
        this._register(this.objectTargets.registerProvider(this._graphTargets));
        this._controller = this._register(new NodeGraphSelectionController<GraphSource>((target) => this._collect(target)));

        // Expose "this graph node can open a shader graph" as a context key so the
        // "Open shader graph" menu item shows ONLY on material nodes that have one
        // (set from the clicked node BEFORE the menu evaluates its `when`).
        const canOpenShader = contextKeyService.createKey<boolean>("context.nodegraphNodeHasShader", false);
        this._register(contextMenuService.onWillShowContextMenu((e) => {
            canOpenShader.set(e.context.targets.some((t) =>
                !!t && typeof t === "object"
                && (t as { nodegraphNode?: boolean }).nodegraphNode === true
                && (t as { meta?: { canStepToShader?: unknown } }).meta?.canStepToShader === true));
        }));
        // Materials changed → drop the memoized graphs and signal (no build here).
        this._register(this._controller.onDidChange(() => this._invalidate()));
        // A fresh GPU measurement changes what the pass cards say — rebuild them
        // from the frame we already have, so the graph and the Performance panel
        // never disagree about the same numbers.
        this._register(this.gpuTiming.onDidMeasure(() => this._refreshRenderGraph()));

        // ONE input: the inspection service's BASE targets — the hierarchy
        // selection or an explicit inspect request (Resources click, reference
        // link). Overrides (drill-down levels, clicked function nodes) are
        // ignored: the node-graph panel owns drill-down display locally, and
        // rebuilding the root from an override would reset the user's trail.
        // Lock lives in the controller: while locked, setTarget is remembered
        // but not applied (unlock resyncs) — same policy as before.
        this._register(inspection.onDidTargetsChange((e) => {
            if (!e.baseChanged) return; // override-only change (code panel concern)
            this._controller.setTarget(inspection.getTargets("base"));
        }));
        // Prime with the current targets so the panel reflects them when opened.
        this._controller.setTarget(inspection.getTargets("base"));

        // Observe every renderer from discovery (see _observeRenderer) — the
        // standing last-frame snapshots are what keep on-demand pages
        // debuggable after they pause.
        this._register(this.hierarchy.onNodeAdded((node) => this._observeRenderer(node.owner)));
        for (const node of this.hierarchy.iterateNodes()) this._observeRenderer(node.owner);

        // A committed STRUCTURAL edit to a graphed node (op/method/const value —
        // anything but a live uniform) must recompile its material and re-derive
        // the graph so the change takes effect and the cards update.
        this._register(this.propertyService.propertyChanged((e) => this._onNodePropertyChanged(e)));
    }

    /** node (any card's underlying TSL node) → its owning material, rebuilt each
     *  capture in {@link _attachEditors}. Weak: never retains host materials. */
    private readonly _nodeMaterial = new WeakMap<object, NodeMaterialLike>();

    private _onNodePropertyChanged(e: PropertyChangedEvent): void {
        // previews (live drag/type ticks) + visibility bookkeeping: wait for the
        // commit, ignore the rest
        if (e.transient || e.hiddenOnly) return;
        const owner = e.property.owner;
        const material = this._nodeMaterial.get(owner);
        if (!material) return; // not a node in the current graph
        const uniform = isUniformNode(owner);
        this.logger.info(`[nodegraph] node edit "${e.property.key}" — material=${!!material} uniform=${uniform}`);
        // uniform values upload live every frame — no shader change to recompile
        if (uniform) return;
        // A constant is baked into the shader, so the RECOMPILE is what actually
        // gets the new value to the GPU.
        (owner as { needsUpdate?: boolean }).needsUpdate = true; // visible signal + harmless
        this._forceMaterialRecompile(material);
        // The REBUILD only re-derives our NodeGraph model (cards/titles/topology)
        // — it does nothing to the material. For a VALUE edit the topology is
        // identical, so it was pure cost: it destroyed and recreated the cards
        // including the live input being edited, which killed the colour picker's
        // own input mid-gesture ("Cannot read properties of null (reading
        // 'value')") and lost the edit. Nothing goes stale by skipping it either —
        // an editable node renders the live editor, never the baked `meta.value`
        // preview, and a constant's title is just "Constant". Structural edits
        // (op/method) still rebuild.
        if (e.property.key !== "value") this._rebuildGraph();
    }

    /** Force a NodeMaterial to rebuild after a structural edit (see
     * {@link forceNodeMaterialRecompile} for the mechanism), with diagnostics. */
    private _forceMaterialRecompile(material: NodeMaterialLike): void {
        const m = material as MaterialRecompileHandle & { type?: string; version?: number };
        const rev = forceNodeMaterialRecompile(m);
        // `customProgramCacheKey()` is DIAGNOSTIC ONLY, and it is not safe to call
        // outside a render: three's WebGL nodes handler resolves per-material
        // render state inside it (`sceneContext`), which is undefined when we call
        // it from an edit — so it threw, and because the call sat inside the LOG
        // line the throw propagated out and killed the whole property edit
        // ("Cannot read properties of undefined (reading 'sceneContext')").
        // The recompile itself already happened above; this is just reporting.
        let cacheKey: unknown = "(not sampled)";
        try {
            cacheKey = m.customProgramCacheKey?.();
        } catch (err) {
            cacheKey = `(unavailable: ${(err as Error)?.message ?? "threw"})`;
        }
        this.logger.info(`[nodegraph] recompile ${m.type ?? "material"} → rev ${rev}, material.version=${m.version}, cacheKey=${cacheKey}`);
    }

    /**
     * Re-derive the material graphs from the (now-mutated) live materials so
     * card titles/structure reflect a structural edit. Lighter than
     * {@link _invalidate}: keeps the capture SOURCE and the user's drill-down
     * (the root's viewportSignature is unchanged), only dropping the memoized
     * build.
     */
    private _rebuildGraph(): void {
        this._built = false;
        this._graphsCache = null;
        this._generation++;
        this._onDidChange.fire();
    }

    /** Cheap: node materials + renderers across the base targets (deduped);
     *  bare textures/geometries (no shader graph) resolve to their registry
     *  entry for the resource dependency graph. */
    private _collect(target: unknown): GraphSource[] {
        const refs = target as InspectionTargetRef[] | null | undefined;
        if (!refs || !refs.length) return [];
        const owners = refs.map((ref) => ref.target);
        const resources = this._resources;
        const sources: GraphSource[] = [];

        // WHAT you selected decides the graph. A material ASSET selected directly
        // shows its resource DEPENDENCY graph (its TSL shader graph is a drill-in
        // — double-click / context menu); an OBJECT that CARRIES a node material
        // shows that material's SHADER graph (you're inspecting the rendered
        // thing). An unregistered node material falls back to its shader graph so
        // selecting it is never a dead end.
        const objects: unknown[] = [];
        for (const owner of owners) {
            if (!owner || typeof owner !== "object") continue;
            const entry = resources?.getEntry(owner);
            // Any registered MATERIAL asset (node OR plain MeshStandardMaterial —
            // dependency/memory applies to both) → its dependency graph. Its TSL
            // shader graph, when it has one, is a drill-in. An unregistered node
            // material falls back to its shader graph so it's never a dead end.
            if (entry?.kind === "material") {
                sources.push({ kind: "resource", entry });
            } else if (isNodeMaterial(owner)) {
                sources.push({ kind: "material", material: owner as NodeMaterialLike });
            } else {
                objects.push(owner);
            }
        }
        for (const material of collectNodeMaterials(objects)) sources.push({ kind: "material", material });
        for (const renderer of collectRenderers(owners)) sources.push({ kind: "renderer", renderer });

        // Other registered resources (texture / geometry / loaded MODEL) → their
        // dependency graph, only when nothing above already claimed the panel.
        if (resources && !sources.length) {
            for (const owner of objects) {
                if (!owner || typeof owner !== "object") continue;
                const entry = resources.getEntry(owner);
                if (entry && entry.kind !== "material") sources.push({ kind: "resource", entry });
            }
        }
        return sources;
    }

    private _invalidate(): void {
        this._override = null; // a real target change supersedes any explicit override
        this._built = false;
        this._graphsCache = null;
        // the displayed mixers (if any) are going away — stop ticking until the
        // next build says otherwise
        this._animationSources = [];
        this._syncAnimationTicker();
        this._lastSkipped = 0;
        this._capturedGraph = null;
        this._displayedCachedCapture = null; // (the per-renderer snapshots persist — weakly)
        this._generation++; // stale-marks any in-flight capture
        this._onDidChange.fire();
    }

    /**
     * Rebuild the displayed render graph — same frame, new numbers.
     *
     * Cheaper than {@link _invalidate}: the selection has not moved, so no
     * generation bump and no override reset. The rebuild re-derives the graph
     * from the observed frame (with the new timing) and re-arms a capture.
     */
    private _refreshRenderGraph(): void {
        if (!this._sourceRenderers?.length) return; // no render graph on display
        this._built = false;
        this._graphsCache = null;
        this._capturedGraph = null;
        this._displayedCachedCapture = null;
        this._onDidChange.fire();
    }

    /** The most recent snapshot that included any of the given renderers. */
    private _lastCaptureFor(renderers: readonly object[]): NodeGraph | null {
        // The standing observer's frame is always as fresh or fresher than any
        // demanded capture (it records every frame, including theirs).
        for (const renderer of renderers) {
            const frame = this._lastFrameByRenderer.get(renderer);
            if (frame) return frameResultToGraph(frame, renderers, this.gpuTiming?.last, this.performance?.targetFps);
        }
        for (const renderer of renderers) {
            const graph = this._lastCaptureByRenderer.get(renderer);
            if (graph) return graph;
        }
        return null;
    }

    private _reportSkipped(skipped: number): void {
        if (skipped > 0) {
            const message = `Node graph too large: ${skipped} material graph(s) not shown (selection exceeds ${MAX_TOTAL_GRAPH_NODES} nodes).`;
            this.logger.warn(`[nodegraph] ${message}`);
            // Only notify when truncation starts, not on repeated reads.
            if (this._lastSkipped === 0) this.notifications.warn(message);
        }
        this._lastSkipped = skipped;
    }

    /**
     * For each leaf value node the adapter tagged as editable, build an
     * `IProperty` targeting its `.value` and stash it on the node's meta. Setting
     * `property.value` routes through SetPropertyCommand (undo/redo); we mark the
     * material dirty on change so the edit takes effect.
     */
    private readonly _graphTargets: GraphObjectTargets;

    private _attachEditors(graph: NodeGraph, material: NodeMaterialLike): void {
        for (const node of graph.nodes) {
            // record EVERY card's underlying TSL node → material, so a structural
            // edit on any inspected node can find its material to recompile
            const tslNode = node.meta?.node;
            if (tslNode && typeof tslNode === "object") this._nodeMaterial.set(tslNode, material);
            if (tslNode && typeof tslNode === "object" && node.sourcePath) {
                this._graphTargets.bind(tslNode, material, node.sourcePath);
            }

            const editableNode = node.meta?.editableNode;
            if (!editableNode || !node.sourcePath) continue;
            node.meta!.property = this._makeValueProperty(editableNode, material, node.sourcePath);
        }
    }

    private _makeValueProperty(node: object, _material: NodeMaterialLike, sourcePath: readonly string[]): Property {
        const value = (node as { value?: unknown }).value;
        const metadata = { ...getMetadata(node, "value", value), editable: true,
            address: buildAddress(_material.uuid, [...sourcePath, "value"]), addressOwner: _material };
        // Uniforms and constants are editable (see the adapter). A uniform uploads
        // its value every frame, so setting `.value` shows on the next render with
        // no recompile; a constant is baked into the shader, so `_onNodeProperty
        // changed` recompiles the material on commit (transient drag ticks skip it).
        const property = new Property(node, "value", valuePropertyType(value), metadata, this.accessor);
        // Graph edits mirror the property gate: free scrubbing on public sites,
        // an inspector license (Needle Inspector Pro / Engine Pro) on local
        // projects. The write is vetoed and the two-stage upsell shown when
        // unlicensed; the detail string attributes the funnel to the trigger.
        const nodeName = (node as { name?: string }).name;
        const material = _material as { name?: string; type?: string };
        const kind = isUniformNode(node) ? "uniform" : "constant";
        const editDetail =
            `edit ${nodeName || valuePropertyType(value)} ${kind}` +
            ` on ${material.name || material.type || "material"}`;
        property.applyGuard = () => this._guardEdit(editDetail);
        // Card properties are created here, outside PropertyService's event forwarding.
        property.onDidChange(e => {
            this._onNodePropertyChanged(e);
            this._logEdit(property.type);
        });
        return property;
    }

    /** Last `nodegraph.edit` timestamp — a slider drag fires per tick, log once per burst. */
    private _lastEditLogged = 0;

    private _logEdit(valueType: string): void {
        const now = Date.now();
        if (now - this._lastEditLogged < 4000) return;
        this._lastEditLogged = now;
        this.analytics.logEvent("nodegraph.edit", { valueType, loggedIn: this.identity.isLoggedIn });
    }

    /** Guard for {@link Property.applyGuard}: public scrubbing stays free, local
     *  projects need an inspector license — the identity/license/upsell dance is
     *  the shared {@link IEditEntitlementService} (one gate for every edit
     *  surface). `detail` attributes the funnel to the exact trigger. */
    private _guardEdit(detail?: string): boolean {
        return this.entitlement.guardEdit({
            source: "nodegraph_edit",
            detail,
            // historical event name — dashboards segment on it
            analyticsEvent: "nodegraph.edit.license_required",
            message: "Editing node-graph values on local projects requires Needle Inspector Pro (included with Needle Engine Pro). Sign in with a free account to continue.",
            fallbackWarning: "Editing node-graph values on local projects requires Needle Inspector Pro.",
        });
    }

    setLocked(locked: boolean): void { this._controller.setLocked(locked); }
    toggleLock(): void {
        this._controller.toggleLock();
        this.analytics.logEvent("nodegraph.lock", { locked: this.locked });
    }

    selectGraphNode(
        meta: Record<string, unknown> | null | undefined,
        options?: { multiSelect?: boolean; domain?: string; category?: string; steppable?: boolean },
    ): boolean {
        const resource = meta?.resource as { deref?: () => object | undefined } | undefined;
        const target = typeof resource?.deref === "function" ? resource.deref() : undefined;
        const hierarchyNode = target ? HierarchyNode.get(target) : undefined;
        // What do people actually click once a graph is open? Logged for EVERY
        // click, including the ones that select nothing — most graph nodes
        // (textures, TSL ops, targets, animation actions) aren't scene objects, so
        // `resolved: false` is the interesting half: it says people are clicking
        // things that do nothing. ONE event with structured props, never the node
        // TITLE (titles are resource names = third-party page content).
        this.analytics.logEvent("nodegraph.node.click", {
            domain: options?.domain ?? "unknown",
            category: options?.category ?? "unknown",
            resolved: !!hierarchyNode,
            steppable: options?.steppable === true,
        });
        if (!target) return false;
        if (!hierarchyNode) {
            this.logger.debug("[nodegraph] clicked resource is not in the hierarchy, cannot select");
            return false;
        }
        // Lock BEFORE selecting: locking is silent (only unlock re-syncs) and a
        // locked controller ignores setTarget, so the displayed graph survives
        // the selection change it is about to cause. Fire so views sync the icon.
        if (!this.locked) {
            this._controller.setLocked(true);
            this._onDidChange.fire();
        }
        this.analytics.logEvent("nodegraph.select-resource", { kind: (resource as { kind?: string }).kind ?? "unknown" });
        // The hierarchy's own selection command — undoable like a hierarchy click.
        this.commands.executeCommand<SelectNodeCommand>(SelectNodeCommand.ID, {
            node: hierarchyNode,
            options: { multiSelect: options?.multiSelect === true },
        });
        return true;
    }

    setConsumerVisible(visible: boolean): void {
        if (this._consumerVisible === visible) return;
        this._consumerVisible = visible;
        // Becoming visible: signal so views re-read `graphs`, which now builds
        // whatever selection accumulated while the panel was hidden.
        if (visible) this._onDidChange.fire();
        this._syncAnimationTicker();
    }

    // #region live animation (mixer graphs)

    private readonly _onDidChangeAnimation = this._register(new Emitter<void>());
    readonly onDidChangeAnimation = this._onDidChangeAnimation.event;
    /** mixer entries backing the currently displayed graphs (weak — never retains) */
    private _animationSources: Array<{ graphIndex: number; rootNodeId: string; entry: IResourceEntry }> = [];
    private _liveAnimation: Map<string, LiveNodeState> | null = null;
    private _animationTimer: ReturnType<typeof setInterval> | undefined;

    getLiveAnimation(): ReadonlyMap<string, LiveNodeState> | null { return this._liveAnimation; }

    /** Run the ticker only while a mixer graph is actually on screen — a hidden
     *  dock tab or a graph without animation costs nothing. */
    private _syncAnimationTicker(): void {
        const wanted = this._consumerVisible && this._animationSources.length > 0;
        if (wanted === (this._animationTimer !== undefined)) return;
        if (wanted) {
            this._tickAnimation(); // publish immediately, don't wait a full period
            this._animationTimer = setInterval(() => this._tickAnimation(), ANIMATION_TICK_MS);
        } else {
            clearInterval(this._animationTimer);
            this._animationTimer = undefined;
            this._liveAnimation = null;
        }
    }

    /** Re-read every displayed mixer and publish the new values. Topology is
     *  untouched — only {@link onDidChangeAnimation} fires. */
    private _tickAnimation(): void {
        const state = new Map<string, LiveNodeState>();
        let alive = false;
        for (const { graphIndex, rootNodeId, entry } of this._animationSources) {
            const live = entry.alive ? entry.deref() : undefined;
            if (!live) continue; // collected mid-view — its rows simply stop updating
            const info = mixerAnimation(live);
            if (!info) continue;
            alive = true;
            state.set(`${graphIndex}:${rootNodeId}`, {
                actionCount: info.actions.length,
                activeCount: info.actions.filter((a) => a.running && a.weight > 0).length,
                timeLabel: `${info.time.toFixed(2)}s`,
            });
            info.actions.forEach((a, i) => {
                const weight = Math.min(1, Math.max(0, a.weight));
                state.set(`${graphIndex}:a${i}`, {
                    running: a.running,
                    paused: a.paused,
                    weight,
                    weightLabel: `${Math.round(weight * 100)}%`,
                    progress: a.duration > 0 ? Math.min(1, Math.max(0, a.time / a.duration)) : 0,
                    timeLabel: `${a.time.toFixed(2)}s / ${a.duration.toFixed(2)}s`,
                });
            });
        }
        this._liveAnimation = state;
        this._onDidChangeAnimation.fire();
        // every mixer was collected — stop burning a timer on nothing
        if (!alive) { this._animationSources = []; this._syncAnimationTicker(); }
    }

    override dispose(): void {
        clearInterval(this._animationTimer);
        this._animationTimer = undefined;
        super.dispose();
    }

    private _navigation: NodeGraphNavigationState | null = null;
    get navigation(): NodeGraphNavigationState | null { return this._navigation; }
    reportNavigation(state: NodeGraphNavigationState | null): void {
        this._navigation = state;
    }
}

/** True when any mesh in the scene's subtree uses `material` (own or in an array). */
function sceneContainsMaterial(scene: unknown, material: unknown): boolean {
    let found = false;
    const traverse = (scene as { traverse?: (cb: (o: unknown) => void) => void } | null)?.traverse;
    if (typeof traverse !== "function") return false;
    traverse.call(scene, (o: unknown) => {
        if (found) return;
        const m = (o as { material?: unknown }).material;
        if (m === material || (Array.isArray(m) && m.includes(material))) found = true;
    });
    return found;
}

/** Property type that drives the Inspector's editor dispatch for a value. */
function valuePropertyType(value: unknown): string {
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    const v = value as Record<string, unknown> | null;
    if (v?.isColor) return "color";
    if (v?.isVector2 || v?.isVector3 || v?.isVector4) return "vector";
    return "object";
}

// SyncDescriptor form (not the ctor overload) so injected non-branded services
// like IPropertyAccessorService are accepted; `false` = eager instantiation
// (this build's InstantiationService has no delayed path, and the service is
// injected into Application so it's created at startup regardless).
registerSingleton(INodeGraphInspectionService, new SyncDescriptor(NodeGraphInspectionService, [], false));
